import { createFileRoute, Link, useSearch, useNavigate } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useSubscription, type Tier } from "@/hooks/useSubscription";
import { createCheckoutSession, createBillingPortalSession } from "@/lib/billing.functions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertTriangle, Clock, Download, Check } from "lucide-react";

type Interval = "month" | "year";
type CardTier = "starter" | "pro" | "clinics";

const PLAN_CARDS: {
  tier: CardTier;
  name: string;
  tagline: string;
  monthly: string;
  annual: string;
  annualBilling: string | null;
  bullets: string[];
  popular: boolean;
  footnote: string | null;
}[] = [
  {
    tier: "starter",
    name: "Starter",
    tagline: "For coaches getting started.",
    monthly: "$0",
    annual: "$0",
    annualBilling: null,
    bullets: [
      "Unlimited clients · 5 visible at a time",
      "3 AI-written client emails per week",
      "Booking page for one session type",
      "Client notes & history",
    ],
    popular: false,
    footnote: null,
  },
  {
    tier: "pro",
    name: "Pro",
    tagline: "For working coaches.",
    monthly: "$12",
    annual: "$10",
    annualBilling: "$120/yr (2 months free)",
    bullets: [
      "Unlimited clients",
      "Unlimited AI-written client emails",
      "Full scheduling with cascading hours — your day fills front-to-back, no gaps",
      "Invoices & automatic reminders",
      "Everything in Starter",
    ],
    popular: true,
    footnote: null,
  },
  {
    tier: "clinics",
    name: "Clinics",
    tagline: "For coaches who run group events.",
    monthly: "$19",
    annual: "$15.83",
    annualBilling: "$190/yr (2 months free)",
    bullets: [
      "Create & sell clinics",
      "Registration, capacity limits & waitlists",
      "Group payments collected for you",
      "Everything in Pro",
    ],
    popular: false,
    footnote: "One 10-player clinic pays for a full year.",
  },
];

export const Route = createFileRoute("/app/settings/billing")({
  head: () => ({
    meta: buildAppMeta({
      title: "Billing — CoachQuick",
      description:
        "Manage your CoachQuick subscription, payment method, invoices, and plan changes.",
      path: "/app/settings/billing",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/settings/billing` }],
  }),
  validateSearch: (
    s: Record<string, unknown>,
  ): { checkout?: "success" | "canceled"; reason?: string } => {
    const out: { checkout?: "success" | "canceled"; reason?: string } = {};
    if (s.checkout === "success" || s.checkout === "canceled") out.checkout = s.checkout;
    if (typeof s.reason === "string") out.reason = s.reason;
    return out;
  },
  component: () => (
    <RequireAuth role="coach">
      <BillingPage />
    </RequireAuth>
  ),
});

function fmtDate(d: Date | null): string {
  if (!d) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function currentPlanName(state: string, tier: Tier): string {
  if (state === "trial") return "CoachQuick Clinics — Trial";
  if (state === "starter") return "CoachQuick Starter";
  if (state === "active" || state === "grace") {
    return tier === "clinics" ? "CoachQuick Clinics" : "CoachQuick Pro";
  }
  return "CoachQuick";
}

function BillingPage() {
  const sub = useSubscription();
  const search = useSearch({ from: "/app/settings/billing" });
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [interval, setInterval] = useState<Interval>("month");
  const handledCheckoutRef = useRef(false);

  useEffect(() => {
    if (search.checkout !== "success" || handledCheckoutRef.current) return;
    handledCheckoutRef.current = true;
    toast.success("Subscription started — you're on your 30-day free trial.");

    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      while (!cancelled && attempts < 8) {
        attempts++;
        const r = await sub.refetch();
        const st = r.data?.state?.state;
        if (st === "active" || st === "trial" || st === "grace") break;
        await new Promise((res) => setTimeout(res, 2000));
      }
    };
    void poll();

    navigate({ to: "/app/settings/billing", search: {}, replace: true });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.checkout]);

  async function authHeaders() {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined;
  }

  async function startCheckout(tier: "pro" | "clinics", chosenInterval: Interval) {
    const busyKey = `checkout:${tier}:${chosenInterval}`;
    setBusy(busyKey);
    try {
      const headers = await authHeaders();
      const { url } = await createCheckoutSession({
        data: { origin: window.location.origin, tier, interval: chosenInterval },
        headers,
      });
      window.location.href = url;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not start checkout");
      setBusy(null);
    }
  }

  async function openPortal() {
    setBusy("portal");
    try {
      const headers = await authHeaders();
      const { url } = await createBillingPortalSession({
        data: { origin: window.location.origin },
        headers,
      });
      window.location.href = url;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not open billing portal");
      await sub.refetch();
      setBusy(null);
    }
  }

  const state = sub.state;
  const tier = sub.tier;

  // Status pill, styled for the always-dark hero panel.
  let statusBadge: { label: string; icon: typeof CheckCircle2; style: CSSProperties } = {
    label: "Loading",
    icon: Clock,
    style: { background: "rgba(255,255,255,.12)", color: "rgba(255,255,255,.8)" },
  };
  if (state === "trial") {
    statusBadge = {
      label: `Clinics trial — ${sub.daysRemaining} ${sub.daysRemaining === 1 ? "day" : "days"} left`,
      icon: Clock,
      style: {
        background: "color-mix(in oklab, var(--primary) 18%, transparent)",
        color: "var(--primary)",
      },
    };
  } else if (state === "active") {
    statusBadge = {
      label: sub.cancelAtPeriodEnd ? "Active (canceling)" : "Active",
      icon: CheckCircle2,
      style: {
        background: "color-mix(in oklab, var(--success) 22%, transparent)",
        color: "oklch(0.8 0.16 145)",
      },
    };
  } else if (state === "grace") {
    statusBadge = {
      label: "Payment failed",
      icon: AlertTriangle,
      style: { background: "rgba(245,158,11,.18)", color: "#fbbf24" },
    };
  } else if (state === "starter") {
    statusBadge = {
      label: "Free plan",
      icon: CheckCircle2,
      style: { background: "rgba(255,255,255,.12)", color: "rgba(255,255,255,.8)" },
    };
  }

  const planName = currentPlanName(state, tier);

  let subline: string | null = null;
  if (state === "trial" && sub.trialEndsAt) {
    subline = `Full Clinics access · ${sub.daysRemaining} ${sub.daysRemaining === 1 ? "day" : "days"} left · Trial ends ${fmtDate(sub.trialEndsAt)}`;
  } else if ((state === "active" || state === "grace") && sub.currentPeriodEnd) {
    const label = sub.cancelAtPeriodEnd ? "Ends on" : "Next billing";
    subline = `${label} ${fmtDate(sub.currentPeriodEnd)}`;
  } else if (state === "starter") {
    subline = "Free forever · upgrade anytime";
  }

  const showPlanPicker =
    state === "trial" || state === "starter" || state === "active" || state === "grace";
  const showPortalButton = sub.hasStripeCustomer;

  const pickerTitle =
    state === "starter"
      ? "Upgrade your account"
      : state === "trial"
        ? "Choose a plan"
        : "Change plan";
  const pickerCopy =
    state === "trial"
      ? "Lock in your plan during the trial. You won't be charged until your 30-day trial ends."
      : state === "starter"
        ? "You're on the free Starter plan. Upgrade to unlock the full toolkit."
        : "Switch tiers anytime. Stripe handles proration automatically.";

  function ctaFor(cardTier: "pro" | "clinics"): {
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    variant: "default" | "outline" | "ghost";
    busyKey?: string;
  } {
    const paidTier = cardTier;
    const busyKey = `checkout:${paidTier}:${interval}`;
    // Currently on this exact paid tier
    if ((state === "active" || state === "grace") && tier === paidTier) {
      return { label: "Current plan", disabled: true, variant: "outline" };
    }
    // Currently on a different paid tier → switch via portal
    if ((state === "active" || state === "grace") && tier !== paidTier && tier !== "starter") {
      return {
        label: `Switch to ${paidTier === "clinics" ? "Clinics" : "Pro"}`,
        onClick: openPortal,
        variant: cardTier === "pro" ? "default" : "outline",
        busyKey: "portal",
      };
    }
    // Trial or Starter → checkout
    const label =
      state === "starter" ? "Upgrade" : state === "trial" ? "Start subscription" : "Subscribe";
    return {
      label,
      onClick: () => startCheckout(paidTier, interval),
      variant: "default",
      busyKey,
    };
  }

  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        <div className="flex flex-col gap-6">
          {/* Breadcrumb + heading */}
          <div>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/app/settings">Settings</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Subscription</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tight md:text-4xl">
              Subscription
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Manage your subscription and payment method.
            </p>
          </div>

          {/* Current-plan hero */}
          <section className="flex flex-wrap items-end justify-between gap-6 rounded-[20px] bg-ink-panel px-7 pb-6 pt-7 text-white">
            <div className="min-w-[260px]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">
                Current plan
              </p>
              <div className="mt-2.5 flex flex-wrap items-center gap-3">
                <h2 className="font-display text-[34px] font-black leading-tight tracking-[-0.02em]">
                  {planName}
                </h2>
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                  style={statusBadge.style}
                >
                  <statusBadge.icon className="h-3.5 w-3.5" />
                  {statusBadge.label}
                </span>
              </div>
              {subline && <p className="mt-3 text-sm text-white/70">{subline}</p>}
            </div>
            {showPortalButton && (
              <Button onClick={openPortal} disabled={busy !== null}>
                {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {busy === "portal"
                  ? "Opening…"
                  : state === "grace"
                    ? "Update payment method"
                    : "Manage subscription"}
              </Button>
            )}
          </section>

          {/* Data-export strip */}
          <section className="flex flex-wrap items-center gap-3 rounded-[16px] border bg-card px-4 py-3.5 sm:px-[18px]">
            <Download className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Your data is always yours, even if you cancel.
            </span>
            <Link
              to="/app/settings/export"
              className="ml-auto text-sm font-medium text-brand-text underline-offset-4 hover:underline"
            >
              Export your data →
            </Link>
          </section>

          {/* Plan picker */}
          {showPlanPicker && (
            <section className="mt-2">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="font-display text-[22px] font-extrabold tracking-[-0.02em]">
                    {pickerTitle}
                  </h2>
                  <p className="mt-1.5 max-w-[44rem] text-sm text-muted-foreground [text-wrap:pretty]">
                    {pickerCopy}
                  </p>
                </div>
                <div className="inline-flex gap-1 rounded-full border bg-muted p-1 text-sm">
                  <button
                    type="button"
                    onClick={() => setInterval("month")}
                    className={`rounded-full px-4 py-1.5 font-medium transition ${
                      interval === "month"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground"
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => setInterval("year")}
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 font-medium transition ${
                      interval === "year"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground"
                    }`}
                  >
                    Annual <span className="text-xs text-brand-text">Save ~17%</span>
                  </button>
                </div>
              </div>

              <div className="mt-6 grid gap-5 md:grid-cols-3">
                {PLAN_CARDS.map((plan) => {
                  const isPaidState = state === "active" || state === "grace";
                  const cta = plan.tier === "starter" ? null : ctaFor(plan.tier);
                  const isBusy = cta?.busyKey ? busy === cta.busyKey : false;
                  return (
                    <div
                      key={plan.tier}
                      className={`relative flex flex-col rounded-[16px] bg-card p-6 shadow-soft ${
                        plan.popular ? "border-2 border-primary" : "border border-border"
                      }`}
                    >
                      {plan.popular && (
                        <span className="absolute -top-[13px] left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                          Most Popular
                        </span>
                      )}
                      <PlanCardBody plan={plan} interval={interval} />
                      {plan.footnote && (
                        <p className="mb-4 text-[13px] italic text-muted-foreground">
                          {plan.footnote}
                        </p>
                      )}

                      {plan.tier === "starter" ? (
                        isPaidState ? (
                          <p className="mt-auto text-center text-xs text-muted-foreground">
                            Downgrade available from Stripe portal after cancellation.
                          </p>
                        ) : (
                          <Button
                            variant={state === "starter" ? "outline" : "ghost"}
                            className="mt-auto w-full"
                            disabled
                          >
                            {state === "starter" ? "Current plan" : "Auto-downgrade at trial end"}
                          </Button>
                        )
                      ) : (
                        <Button
                          variant={cta!.variant}
                          className="mt-auto w-full"
                          disabled={cta!.disabled || (busy !== null && !isBusy)}
                          onClick={cta!.onClick}
                        >
                          {isBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                          {cta!.label}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Test-mode banner */}
          {sub.testMode && (
            <section className="flex items-start gap-2.5 rounded-[16px] border border-amber-300/40 bg-amber-50 px-4 py-3.5 text-sm text-amber-900 sm:px-[18px] dark:border-amber-500/30 dark:bg-amber-900/20 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong className="font-semibold">Test mode:</strong> use card{" "}
                <span className="font-mono">4242 4242 4242 4242</span>, any future expiry, any
                3-digit CVC. No real money moves.
              </span>
            </section>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function PlanCardBody({
  plan,
  interval,
}: {
  plan: (typeof PLAN_CARDS)[number];
  interval: Interval;
}) {
  const isAnnual = interval === "year";
  return (
    <>
      <h3 className="font-display text-xl font-extrabold tracking-[-0.02em]">{plan.name}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{plan.tagline}</p>
      <div className="mt-[18px] flex items-baseline gap-1">
        <span className="font-display text-[40px] font-black tracking-[-0.02em]">
          {isAnnual ? plan.annual : plan.monthly}
        </span>
        <span className="text-sm text-muted-foreground">/mo</span>
      </div>
      {isAnnual && plan.annualBilling && (
        <p className="mt-1.5 text-[13px] text-muted-foreground">
          Billed annually at {plan.annualBilling}
        </p>
      )}
      {!isAnnual && plan.monthly !== "$0" && plan.annualBilling && (
        <p className="mt-1.5 text-[13px] text-muted-foreground">
          or {plan.annualBilling} when billed annually
        </p>
      )}
      <ul className="my-[22px] flex flex-1 flex-col gap-3">
        {plan.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 text-sm">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-text" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </>
  );
}
